// Per-step save scoping for the producer editor (CreateEditBriefing).
//
// Background
// ----------
// The editor used to call saveRelatedData() on every Next/Back/Save
// click, rewriting every related-data table (briefing_stages, lineup,
// crew_members, crew_assignments, briefing_crew, briefing_crew_days,
// briefing_day_doors) regardless of which step the producer was on.
// Combined with the replace_* RPCs' missing diff-check (fixed in
// migration 20260521120000+), that produced ~38 unchanged-row
// UPDATEs per lineup row and >80% daily CPU peaks even with no
// festival activity.
//
// SaveScope lets each step's Next/Back button declare which section
// of saveRelatedData should run. Steps that don't write related-data
// tables (Festival map, Live tracking, Review) pass "none"; step
// navigations that genuinely cross multiple sections (Publish, Save
// draft) pass "all".

export type SaveScope = "all" | "stages" | "lineup" | "crew" | "days" | "none";

// Map a step index to the saveRelatedData scope that owns its tables.
// Keep this in sync with PRODUCTION_STEPS in @produce/shared/productionSteps:
//
//   0  Basic information     → "days"  (briefings row via saveProduction, PLUS the
//                                       per-day doors in briefing_day_doors. The
//                                       Festival Days cards live on this step and
//                                       nowhere else, so this step owns that write.
//                                       It used to map to "none": the producer's
//                                       Saturday/Sunday times then never reached
//                                       briefing_day_doors, only
//                                       briefings.doors_open/close — which holds
//                                       day 1's window. Every day re-hydrated from
//                                       that one fallback, so a Fri 16:00 / Sat
//                                       12:00 festival read 16:00 on all days,
//                                       including the crew-schedule timeline,
//                                       which anchors on doors.)
//   1  Stages per day        → "stages"
//   2  Timetable import      → "lineup"
//   3  The crew              → "crew"
//   4  Festival map          → "none"  (handled by saveLiveTrackingData)
//   5  Crew schedules        → "crew"  (writes crew_assignments via the crew section's
//                                       STEP-6 sub-block; no other tables are touched)
//   6  General briefing      → "crew"  (briefings text fields save via
//                                       saveProduction regardless of scope;
//                                       the Camera ID card on this step edits
//                                       crew data — camera_id rides on the
//                                       briefing_crew_days mirror in the crew
//                                       block, so the crew scope must run for
//                                       Camera ID edits to persist, including
//                                       published quick-edits. Gated by the
//                                       crew dirty-check so unrelated saves
//                                       still no-op.)
//   7  Live tracking setup   → "none"  (saveLiveTrackingData runs separately in handleSave)
//   8  Review & publish      → "none"  (Back from review; the Publish button itself
//                                       passes scope "all" explicitly)
//
// Note: the *Publish* button on step 8 stays scope "all" — defensively
// covers the case where the producer jumped past steps. Save-draft
// also stays "all". Both kept by callers, not by this mapper.
export const stepScope = (step: number): SaveScope => {
  switch (step) {
    case 0:
      return "days";
    case 1:
      return "stages";
    case 2:
      return "lineup";
    case 3:
      return "crew";
    case 5:
      return "crew";
    case 6:
      // General briefing hosts the Camera ID card (crew data). The
      // briefing text fields persist via saveProduction regardless of
      // scope; the crew scope is needed so camera_id changes flow
      // through the briefing_crew_days mirror.
      return "crew";
    case 4:
    case 7:
    case 8:
      return "none";
    default:
      return "all";
  }
};

export interface ScopeFlags {
  runStages: boolean;
  runLineup: boolean;
  runCrew: boolean;
  /** Per-day doors (briefing_day_doors). Owned by step 0. */
  runDays: boolean;
}

// Compute which sections of saveRelatedData should execute for a
// given scope. "all" enables everything; named scopes enable only
// their own section; "none" disables everything (the function still
// runs and builds resolveStageId from cache, but issues no DB
// writes).
export const computeScopeFlags = (scope: SaveScope): ScopeFlags => ({
  runStages: scope === "all" || scope === "stages",
  runLineup: scope === "all" || scope === "lineup",
  runCrew: scope === "all" || scope === "crew",
  runDays: scope === "all" || scope === "days",
});

// Stringified snapshots of the state slices a save can touch.
// The editor stringifies its current state and a "last saved"
// reference at this granularity; comparing strings is enough — the
// arrays are plain objects that JSON.stringify roundtrips cleanly.
//
// The "crew" scope owns TWO slices: `crew` (the crew-member list that
// drives crew_members / briefing_crew / briefing_crew_days) and
// `assignments` (the crew schedule itself — crew_assignments). The
// schedule must be tracked separately, otherwise a save whose only
// change is the schedule (the producer dragged set-times around but
// touched no crew member) looks "clean" and gets short-circuited,
// silently dropping the schema on the next refresh.
export interface ScopeStateSnapshot {
  stages: string;
  timetable: string;
  crew: string;
  assignments: string;
  festivalDays: string;
}

// Returns true when the scope's owned state has changed since the
// last save. Used by handleSave to skip the entire save round when a
// Next/Back click didn't touch anything the step persists.
//
// "all"  → dirty iff *any* slice changed (defensive; Publish/Save-draft).
// "none" → never dirty (caller skips saveRelatedData entirely).
// other  → dirty iff that scope's owned slices changed.
//
// "days" owns `festivalDays` (for briefing_day_doors). It is a scope
// of its own rather than a slice of "lineup" because the Festival
// Days cards live on step 0 and the timetable import on step 2 —
// one scope, one owner, so neither step writes the other's table.
//
// "crew" owns both `crew` (the member list) and `assignments` (the
// crew schedule). A change to either makes the scope dirty — the
// schedule edits land via the same runCrew section in saveRelatedData.
export const isScopeDirty = (
  scope: SaveScope,
  current: ScopeStateSnapshot,
  saved: ScopeStateSnapshot,
): boolean => {
  switch (scope) {
    case "none":
      return false;
    case "all":
      return (
        current.stages !== saved.stages ||
        current.timetable !== saved.timetable ||
        current.crew !== saved.crew ||
        current.assignments !== saved.assignments ||
        current.festivalDays !== saved.festivalDays
      );
    case "stages":
      return current.stages !== saved.stages;
    case "lineup":
      return current.timetable !== saved.timetable;
    case "crew":
      return current.crew !== saved.crew || current.assignments !== saved.assignments;
    case "days":
      return current.festivalDays !== saved.festivalDays;
  }
};

// Update the "last saved" snapshot for the scope that just succeeded.
// Returns a new snapshot rather than mutating so callers can keep
// React-style immutable refs if they want. "all" rewrites every slice,
// each named scope rewrites only the slices it owns — this helper
// centralises that mapping.
export const captureSavedSnapshot = (
  scope: SaveScope,
  current: ScopeStateSnapshot,
  saved: ScopeStateSnapshot,
): ScopeStateSnapshot => {
  switch (scope) {
    case "none":
      return saved;
    case "all":
      return { ...current };
    case "stages":
      return { ...saved, stages: current.stages };
    case "lineup":
      return { ...saved, timetable: current.timetable };
    case "crew":
      return { ...saved, crew: current.crew, assignments: current.assignments };
    case "days":
      return { ...saved, festivalDays: current.festivalDays };
  }
};
